import { Controller } from "@hotwired/stimulus"

// Visible feedback for a mobile Save that never reached the server
// (CONEK-8541). Mounted on the mobile layout's <body>, so it covers every
// mobile form.
//
// When the request itself fails (connection refused, a dead Wi-Fi, a proxy,
// a dropped connection), Turbo ends the submission with
// `turbo:submit-end` { success: false, error } and no fetchResponse, then
// re-throws the error, which only reaches the console. The page does not
// change, so Save looked like a dead button and nothing told the rep the task
// or call was not saved. This shows an error toast instead. The form and what
// was typed stay as they are, and the Discard guard still sees the form as
// changed. Save is never disabled, so tapping it again retries.
//
// Only the no-response case is handled here. With a response Turbo already
// shows something: a 422 re-renders the form with its errors, and a 5xx
// renders the error page. An aborted request ends with no `success` at all.
//
// It does not collide with bridge--offline-queue: when that controller
// queues an offline write it cancels the submit, so Turbo never sends it and
// no `turbo:submit-end` follows.
//
// The toast is server-rendered (shared/_mobile_save_failed_toast), inert in a
// <template>, and cloned into #mobile-toast: the copy stays in Rails i18n and
// the markup in Ui::ToastComponent, as for the "Saved offline" toast.
export default class extends Controller {
  static targets = ["toast"]

  connect() {
    this.onSubmitEnd = this.onSubmitEnd.bind(this)
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  onSubmitEnd(event) {
    const { success, error, fetchResponse } = event.detail || {}
    if (success !== false || !error || fetchResponse) return

    this.showToast()
  }

  // One toast at a time: a repeat failure replaces the one still showing
  // rather than stacking copies of the same message.
  showToast() {
    const container = document.getElementById("mobile-toast")
    const toast =
      this.hasToastTarget &&
      this.toastTarget.content.firstElementChild?.cloneNode(true)
    if (!container || !toast) return

    this.shownToast?.remove()
    // The clone carries the id the template was rendered with; give each copy
    // its own, random like ToastComponent's server-side SecureRandom.hex.
    toast.id = `toast-save-failed-${Math.random().toString(36).slice(2, 10)}`
    container.append(toast)
    this.shownToast = toast
  }
}
